// src/App.js
import React, { useEffect, useContext, useState, Suspense } from 'react';
import { isExternalTrackingBlockedPath } from './utils/productionTracking';
import { initializeGoogleTag } from './utils/googleTag';
import useSignedInPublicRedirect from './routes/useSignedInPublicRedirect';
import { ContractStatusProvider } from './contexts/ContractStatusContext';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import NavigationBar from './components/Navbar';
import DemoUserBanner from './components/DemoUserBanner';
import ImpersonationBanner from './components/ImpersonationBanner';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import CompanyLayout from './components/Company/CompanyLayout';
import InterviewFeedbackPrompt from './components/InterviewFeedback/InterviewFeedbackPrompt';
import GroupApprovalGate from './components/Company/GroupApprovalGate';
import CompanyLandingRedirect from './components/Company/CompanyLandingRedirect';
import ProviderEntry from './components/Provider/ProviderEntry';
import TosAcceptanceGate from './components/Auth/TosAcceptanceGate';
import PipelineReviewGate from './components/Company/PipelineReviewGate';
import ProfileCompletionGuard from './components/ProfileCompletionGuard';
import RoleBasedRoute from './components/RoleBasedRoute';
import MedicalStudentAccessGate from './components/Provider/MedicalStudentAccessGate';
import AuthDebug from './components/AuthDebug';
import StepUpProvider from './components/StepUp/StepUpProvider';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { DebugProvider } from './contexts/DebugContext';
import { NotificationProvider } from './contexts/NotificationContext';
import AOS from 'aos';
import 'aos/dist/aos.css';
// Bootstrap CSS is no longer imported here — it's served from
// /vendor-bootstrap.min.css via a non-blocking preload-swap <link> in
// public/index.html. Keeps ~227 KiB of CSS out of the render-blocking
// main.css bundle. The file is copied from node_modules by the
// `prebuild` / `predev` scripts in package.json.
// fullcalendar.css moved into SchedulerComponents.jsx so it only ships
// with the scheduler chunk instead of the main bundle.
import featureFlagService from './services/featureFlagService';
import { captureUtmParams } from './utils/utmCapture';
import { attachSessionContext } from './utils/analyticsContext';
import { identify, resetIdentity } from './utils/dataLayer';
import journeyEvents from './services/journeyEventService';
import { SwipeQueueProvider } from './contexts/SwipeQueueContext';
import { InterviewRequestProvider } from './components/InterviewRequest/InterviewRequestProvider';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Navigate } from 'react-router-dom';
import { CompanyProvider } from './contexts/CompanyContext';
import PageLoader from './components/PageLoader';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import ApiDedupeStatsPanel from './components/DevTools/ApiDedupeStatsPanel';
import JobFeedApprovalBridge from './components/Notifications/JobFeedApprovalBridge';
import RegistrationPageViewTracker from './components/Registration/RegistrationPageViewTracker';
import { AVAILABILITY_SEARCH_ENABLED } from './config/availabilitySearch';
import { PARTNER_CONNECTIONS_ENABLED } from './config/partnerConnections';

// Lazy-load all page components for code splitting
// EContractSignPage + ContractTracker removed per PRODUCT-ETHOS.md D9 (audit P1/P2):
// both rendered fabricated/placeholder contract data with no real backend.
const NegotiationsPage = React.lazy(() => import('./pages/NegotiationsPage'));
const InterviewFeedbackMagicPage = React.lazy(() =>
  import('./pages/InterviewFeedbackMagicPage'),
);
const ProvidersLandingPage = React.lazy(() => import('./pages/ProvidersLandingPage'));
const NewAboutPage = React.lazy(() => import('./pages/NewAboutPage'));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const DemoLandingPage = React.lazy(() => import('./pages/DemoLandingPage'));
const GroupLandingPage = React.lazy(() => import('./pages/GroupLandingPage'));
const CRNADemoPage = React.lazy(() => import('./pages/CRNADemoPage'));
const CrnaLandingPage = React.lazy(() => import('./pages/CrnaLandingPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const Login = React.lazy(() => import('./pages/Login'));
const McpPublicationArrival = React.lazy(() => import('./pages/McpPublicationArrival'));
const McpOAuthCallbackPage = React.lazy(() => import('./pages/McpOAuthCallbackPage'));
const ProfileV2Page = React.lazy(() => import('./pages/ProfileV2Page'));
const GroupDashboardPage = React.lazy(() => import('./pages/GroupDashboardPage'));
const GroupProviderSwipePage = React.lazy(() => import('./pages/GroupProviderSwipePage'));
const TestGroupIntake = React.lazy(() => import('./components/GroupIntake/TestGroupIntake'));
const PremiumGroupMatches = React.lazy(() => import('./components/Listings/PremiumGroupMatches'));
const ListingReviewPage = React.lazy(() => import('./pages/ListingReviewPage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const ReferralShirtRewardsPage = React.lazy(() => import('./pages/admin/ReferralShirtRewardsPage'));
const ReferralActivityRoute = React.lazy(() => import('./pages/admin/ReferralActivityRoute'));
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'));
const AtRiskNegotiationsPage = React.lazy(() => import('./pages/AtRiskNegotiationsPage'));
const InboundLeadsList = React.lazy(() => import('./pages/admin/InboundLeadsList'));
const InboundLeadDetail = React.lazy(() => import('./pages/admin/InboundLeadDetail'));
const InboundLeadFunnel = React.lazy(() => import('./pages/admin/InboundLeadFunnel'));
const ManualLeadEntry = React.lazy(() => import('./pages/admin/ManualLeadEntry'));
const InboundLeadCsvImport = React.lazy(() => import('./pages/admin/InboundLeadCsvImport'));
const VisitorMapPage = React.lazy(() => import('./pages/admin/VisitorMapPage'));
const ProviderRegistrationExperimentPage = React.lazy(() => import('./pages/admin/ProviderRegistrationExperimentPage'));
const RegisterRouter = React.lazy(() => import('./pages/RegisterRouter'));
const ProviderRegistration = React.lazy(() => import('./pages/ProviderRegistration'));
const StartFunnel = React.lazy(() => import('./pages/start/StartFunnel'));
const GroupRegistration = React.lazy(() => import('./pages/GroupRegistration'));
const UserRegistrationInvite = React.lazy(() => import('./pages/UserRegistrationInvite'));
const WelcomeFromLeadPage = React.lazy(() => import('./pages/WelcomeFromLeadPage'));
const MetaLeadBridgePage = React.lazy(() => import('./pages/MetaLeadBridgePage'));
const WelcomeErrorPage = React.lazy(() => import('./pages/WelcomeErrorPage'));
const InvitesPage = React.lazy(() => import('./pages/InvitesPage'));
const InviteRedirect = React.lazy(() => import('./pages/InviteRedirect'));
const UnauthorizedPage = React.lazy(() => import('./pages/UnauthorizedPage'));
const ShareLanding = React.lazy(() => import('./routes/ShareLanding'));
const ShareRoleGate = React.lazy(() => import('./routes/ShareRoleGate'));
const GasWorkShareRedirect = React.lazy(() => import('./routes/GasWorkShareRedirect'));
const RecruiterMatchesDeck = React.lazy(() => import('./pages/RecruiterMatchesDeck'));
const ProviderMatchesDeck = React.lazy(() => import('./pages/ProviderMatchesDeck'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = React.lazy(() => import('./pages/PaymentCancelPage'));
const WhitePaperDownload = React.lazy(() => import('./pages/WhitePaperDownload.jsx'));
const ReferralLanding = React.lazy(() => import('./pages/ReferralLanding.jsx'));
const OAuthCallback = React.lazy(() => import('./pages/OAuthCallback.jsx'));
const AcceptTerms = React.lazy(() => import('./pages/AcceptTerms.jsx'));
const ListingSeoPage = React.lazy(() => import('./pages/ListingSeoPage.jsx'));
const BillingManagementPage = React.lazy(() => import('./pages/BillingManagementPage'));
const SchedulerDemoPage = React.lazy(() => import('./pages/SchedulerDemoPage'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const CredentialingVault = React.lazy(() => import('./components/CredentialingVault/CredentialingVault'));
const VerificationDashboard = React.lazy(() => import('./components/Verification/VerificationDashboard'));
const LegacyClaimRedirect = React.lazy(() => import('./pages/LegacyClaimRedirect'));
const MagicLoginCallback = React.lazy(() => import('./pages/MagicLoginCallback'));
const ProviderEmailMagicLogin = React.lazy(() =>
  import('./pages/ProviderEmailMagicLogin'),
);
const ExternalListingClaimPage = React.lazy(() => import('./pages/ExternalListingClaimPage'));
const GuestNegotiationPage = React.lazy(() => import('./pages/GuestNegotiationPage'));
const RecruiterRatePage = React.lazy(() => import('./pages/RecruiterRatePage'));
const VerifyListingPage = React.lazy(() => import('./pages/VerifyListingPage'));
const VerifyCodePage = React.lazy(() => import('./pages/VerifyCodePage'));
const ListingHealthReportPage = React.lazy(() =>
  import('./pages/ListingHealthReport/ListingHealthReportPage'),
);
const FeedRequisitionHealthPage = React.lazy(() =>
  import('./pages/FeedRequisitionHealthPage'),
);
const SmsConfirmPage = React.lazy(() => import('./pages/SmsConfirmPage'));
const SubmitListingsPage = React.lazy(() => import('./pages/SubmitListingsPage'));
const EmployersAgreementPage = React.lazy(() => import('./pages/EmployersAgreementPage'));
const EmployerSignPage = React.lazy(() => import('./pages/EmployerSignPage'));
const EmployersCreatePage = React.lazy(() => import('./pages/EmployersCreatePage'));
const ListingReportPage = React.lazy(() => import('./pages/ListingReportPage'));
const RecruiterMarketReportPage = React.lazy(() => import('./pages/RecruiterMarketReportPage'));
const ListingSubmissionsList = React.lazy(() => import('./pages/admin/ListingSubmissionsList'));
const VendorDocsRequestsList = React.lazy(() => import('./pages/admin/VendorDocsRequestsList'));
const ListingSubmissionDetail = React.lazy(() => import('./pages/admin/ListingSubmissionDetail'));
const ListingSuppressionsPage = React.lazy(() => import('./pages/admin/ListingSuppressionsPage'));
const McpPublicationOperationsPage = React.lazy(() =>
  import('./pages/admin/McpPublicationOperationsPage'),
);
const AdminWildCapturePage = React.lazy(() => import('./pages/AdminWildCapturePage'));
// The recruiter half of Wild Job Capture: public, token-only, no account.
const PublishListingPage = React.lazy(() => import('./pages/PublishListingPage'));
const CityJobsPage = React.lazy(() => import('./pages/CityJobsPage'));
const StateJobsPage = React.lazy(() => import('./pages/StateJobsPage'));
const CrnaSalaryPage = React.lazy(() => import('./pages/CrnaSalaryPage'));
const MarketReportPage = React.lazy(() => import('./pages/MarketReportPage'));
const StateMarketReportPage = React.lazy(() => import('./pages/StateMarketReportPage'));
const CrnaPracticeAuthorityPage = React.lazy(() => import('./pages/CrnaPracticeAuthorityPage'));
const CrnaMarketOverview = React.lazy(() => import('./pages/CrnaMarketOverview'));
const BlogListPage = React.lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const UnsubscribePage = React.lazy(() => import('./pages/UnsubscribePage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const PublicWatermarkPage = React.lazy(() => import('./pages/PublicWatermarkPage'));
const PublicResumeBuilderPage = React.lazy(() => import('./pages/PublicResumeBuilderPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const MarketInsightsPage = React.lazy(() => import('./pages/MarketInsightsPage'));
const SecureShareLinkPage = React.lazy(() => import('./pages/SecureShareLinkPage'));
const ProviderDashboardV2 = React.lazy(() => import('./pages/ProviderDashboardV2'));
const ProviderProfilePage = React.lazy(() => import('./pages/ProviderProfilePage'));
const MedicalStudentAnesthesiologyPage = React.lazy(() =>
  import('./pages/MedicalStudentAnesthesiologyPage'),
);
const ProviderDiscoverPage = React.lazy(() => import('./pages/ProviderDiscoverPage'));
const ProviderMatchesRedirect = React.lazy(() => import('./routes/ProviderMatchesRedirect'));
const ProviderPipelinePage = React.lazy(() => import('./pages/ProviderPipelinePage'));
const ProviderVaultPage = React.lazy(() => import('./pages/ProviderVaultPage'));
const ProviderListingDetailPage = React.lazy(() => import('./pages/ProviderListingDetailPage'));
const ProviderNegotiationDetailPage = React.lazy(() => import('./pages/ProviderNegotiationDetailPage'));
const ProviderNotificationsPage = React.lazy(() => import('./pages/ProviderNotificationsPage'));
const ProviderFinancialPage = React.lazy(() => import('./pages/ProviderFinancialPage'));
const ProviderMapExplorerPage = React.lazy(() => import('./pages/ProviderMapExplorerPage'));
const ProviderProfileEditorPage = React.lazy(() => import('./pages/ProviderProfileEditorPage'));
const ProviderServicesPage = React.lazy(() => import('./pages/ProviderServicesPage'));
const ProviderServicesShare = React.lazy(() => import('./routes/ProviderServicesShare'));
const ProviderScheduleRedirect = React.lazy(() => import('./routes/ProviderScheduleRedirect'));
const PROVIDER_SCHEDULE_ENABLED =
  String(process.env.REACT_APP_PROVIDER_SCHEDULE_ENABLED ?? 'false').trim().toLowerCase() === 'true';
const ProviderRoundsPage = React.lazy(() => import('./pages/ProviderRoundsPage'));
const PROVIDER_ROUNDS_ENABLED =
  String(process.env.REACT_APP_PROVIDER_ROUNDS_ENABLED ?? 'false').trim().toLowerCase() === 'true';
const RoostPage = React.lazy(() => import('./pages/RoostPage'));
const ROOST_ENABLED = (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('roost') === '1') { localStorage.setItem('roostEnabled', 'true'); }
    if (params.get('roost') === '0') { localStorage.removeItem('roostEnabled'); }
    return (
      localStorage.getItem('roostEnabled') === 'true' ||
      String(process.env.REACT_APP_ROOST_ENABLED ?? 'false').trim().toLowerCase() === 'true'
    );
  } catch (e) {
    return String(process.env.REACT_APP_ROOST_ENABLED ?? 'false').trim().toLowerCase() === 'true';
  }
})();
const FindProvidersPage = React.lazy(() => import('./pages/FindProvidersPage'));
const CoverageArrival = React.lazy(() => import('./features/partner-coverage/CoverageArrival'));
const CoverageAdmin = React.lazy(() => import('./features/partner-coverage/CoverageAdmin'));
const CoverageConnections = React.lazy(() => import('./features/partner-coverage/CoverageConnections'));
const GUIDED_ONBOARDING_ENABLED =
  String(process.env.REACT_APP_GUIDED_ONBOARDING_ENABLED ?? 'false').trim().toLowerCase() === 'true';
const ProviderComparisonPage = React.lazy(() => import('./pages/ProviderComparisonPage'));
const ProviderCredentialDetailPage = React.lazy(() => import('./pages/ProviderCredentialDetailPage'));
const CompanyDashboardPage = React.lazy(() => import('./pages/CompanyDashboardPage'));
const CompanyListingsPage = React.lazy(() => import('./pages/CompanyListingsPage'));
const McpPublicationReviewPage = React.lazy(() => import('./pages/McpPublicationReviewPage'));
const CompanyListingReportPage = React.lazy(() => import('./pages/CompanyListingReportPage'));
const CompanyReportResolver = React.lazy(() => import('./pages/CompanyReportResolver'));

/**
 * The per-listing market report moved from /company/market-report/:listingId to
 * /company/listings/:listingId/market-report when the portfolio page was folded
 * into the listings roster. This keeps any link already in the wild working,
 * carrying the listing id across rather than dropping the reader on the roster
 * and making them find the row again.
 */
const LegacyMarketReportRedirect = () => {
    const { listingId } = useParams();
    return <Navigate to={`/company/listings/${listingId}/market-report`} replace />;
};
const CompanyProvidersPage = React.lazy(() => import('./pages/CompanyProvidersPage'));
const CompanyNegotiationsPage = React.lazy(() => import('./pages/CompanyNegotiationsPage'));
const CompanyNegotiationDetailPage = React.lazy(() => import('./pages/CompanyNegotiationDetailPage'));
const CompanyPipelineReviewPage = React.lazy(() => import('./pages/CompanyPipelineReviewPage'));
const CompanySettingsPage = React.lazy(() => import('./pages/CompanySettingsPage'));
const CompanyNotificationsPage = React.lazy(() => import('./pages/CompanyNotificationsPage'));
// CompanyVaultPage withdrawn: it wrapped the provider-side CredentialingVault,
// so a company account landed on "Your Credential Vault". /company/vault now
// falls through to the company catch-all redirect below.
const MarketingShell = React.lazy(() => import('./marketing/MarketingShell'));
const MarketingOverview = React.lazy(() => import('./marketing/pages/Overview'));
const MarketingJobSocialLibrary = React.lazy(() =>
  import('./marketing/pages/JobSocialLibrary'),
);
const MarketingAnalytics = React.lazy(() => import('./marketing/pages/Analytics'));
const MarketingTracker = React.lazy(() => import('./marketing/pages/Tracker'));
const MarketingNegotiations = React.lazy(() => import('./marketing/pages/Negotiations'));
const MarketingUsers = React.lazy(() => import('./marketing/pages/Users'));
const MarketingUserProfile = React.lazy(() => import('./marketing/pages/UserProfile'));
const MarketingLinks = React.lazy(() => import('./marketing/pages/Links'));
const MarketingOutreach = React.lazy(() => import('./marketing/pages/Outreach'));
const MarketingCommand = React.lazy(() => import('./marketing/pages/MarketCommand'));
const AdminTeam = React.lazy(() => import('./marketing/pages/Team'));

// Define roles for our application
export const ROLES = {
  ADMIN: 'ADMIN',
  CRNA: 'CRNA',
  GROUP: 'GROUP',
  MARKETING: 'MARKETING',
};

// BUG-025: Logout route — clears auth and redirects to login
const LogoutRedirect = () => {
  const { logout } = useContext(AuthContext);
  const nav = useNavigate();
  useEffect(() => {
    logout();
    nav('/login', { replace: true });
  }, [logout, nav]);
  return null;
};

// GROUP users hitting /negotiations/:matchId directly should be redirected
// to the company dashboard so they get the GroupActionBar (company nav).
// CRNAs and admins see the standalone NegotiationsPage.
const NegotiationsRouteGuard = () => {
  const { auth } = useContext(AuthContext);
  const { matchId } = useParams();
  const nav = useNavigate();

  useEffect(() => {
    if (auth?.user?.role === ROLES.GROUP) {
      nav(`/company/pipeline/${matchId}`, { replace: true });
    }
  }, [auth?.user?.role, matchId, nav]);

  // For non-GROUP users, render the NegotiationsPage directly
  if (auth?.user?.role === ROLES.GROUP) {
    return null; // Will redirect
  }

  return <NegotiationsPage />;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const { auth } = useContext(AuthContext);

  // A signed-in visitor of /, /login or /register goes home for their role
  // (a provider: the dashboard, whose journey card leads them on).
  useSignedInPublicRedirect();

  // Give GA4 the signed-in user. No page_view and no Meta PageView from here:
  // GTM records them (GA4 enhanced measurement sees every route change, and
  // GTM's Meta Pixel tag sends PageView and relays every page_view on the
  // dataLayer, where gtag() also pushes), so app copies are counted again.
  useEffect(() => {
    if (isExternalTrackingBlockedPath(location.pathname)) return;
    const gtagAvailable = typeof window !== 'undefined' && typeof window.gtag === 'function';
    if (!gtagAvailable) return;

    const role = auth?.user?.role;
    const userId = auth?.user?.sub;

    if (userId) window.gtag('set', { user_id: userId });
    if (role) window.gtag('set', 'user_properties', { role });
  }, [auth?.user?.role, auth?.user?.sub, location.pathname]);

  // ── Journey Intelligence: wire user identity ────────────────────────────────
  useEffect(() => {
    if (auth?.token && auth?.user) {
      journeyEvents.setAuthToken(auth.token);
      journeyEvents.setUser(auth.user.sub || auth.user.id, auth.user.role);
    } else {
      journeyEvents.clearUser();
    }
  }, [auth?.token, auth?.user]);

  // Capture click interactions for CRNA and GROUP users
  useEffect(() => {
    const handleClick = (event) => {
      if (isExternalTrackingBlockedPath(window.location.pathname)) return;
      const role = auth?.user?.role;
      const userId = auth?.user?.sub;
      if (!window.gtag || !(role === ROLES.CRNA || role === ROLES.GROUP)) return;

      const label =
        event.target?.id ||
        event.target?.name ||
        event.target?.textContent?.trim().slice(0, 100) ||
        event.target?.tagName;

      window.gtag('event', 'interaction', {
        event_category: 'click',
        event_label: label,
        role,
        user_id: userId,
      });
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [auth?.user?.role, auth?.user?.sub]);

  const isDemoPage = location.pathname.startsWith('/demo');
  const isProfilePage = location.pathname === '/profile' || location.pathname === '/profile-new';
  const isGroupDashboardPage = location.pathname.startsWith('/company');
  const isNotificationsPage = location.pathname === '/notifications';
  const isLandingPage = location.pathname === '/employers' || location.pathname === '/providers' || location.pathname === '/market-insights' || location.pathname.startsWith('/lp/');
  const isRegistrationPage = location.pathname === '/register' || location.pathname === '/register/company' || location.pathname === '/register/provider' || location.pathname === '/join' || location.pathname === '/coverage/continue';

  const isNegotiationsRoute = location.pathname.startsWith('/negotiations');
  const isInterviewFeedbackRoute = location.pathname.startsWith('/interview-feedback/');
  const isVaultOrVerification = location.pathname === '/vault' || location.pathname === '/verification';
  const isProviderApp = location.pathname.startsWith('/provider');
  const isShareLanding = location.pathname.startsWith('/s/') && (new URLSearchParams(location.search).has('utm_source') || location.pathname.endsWith('/services') || (location.pathname.endsWith('/role') && new URLSearchParams(location.search).has('svc')));
  const isToolsPage = location.pathname.startsWith('/tools');
  // FNI-5: guest negotiation entry. No nav/footer — this is a recruiter's
  // first touch with RxRooster via an email CTA and needs to feel like a
  // direct, focused response surface, not a marketing site.
  const isFniGuestRoute = location.pathname.startsWith('/negotiate/invite/');
  // Wild Job Capture's publish invite: a recruiter opens her own listing card
  // from an emailed link with no account and publishes it by registering.
  // Chromeless for the same reason as the two guest routes around it -- it is a
  // focused response to one email, not a marketing site, and the card needs the
  // vertical space a navbar and a footer would take.
  const isPublishDraftRoute = location.pathname.startsWith('/publish/');
  // Comp-blind rate capture (Phase 2): no-login recruiter rate-entry surface.
  // Chromeless like the guest negotiation and claim pages: a recruiter's
  // focused response to an interest email, not a marketing site.
  const isRecruiterRateRoute = location.pathname.startsWith('/rate/');
  // Includes the bare /verify entry-code door (short-code second entry point
  // into the same no-login flow), not just the /verify/:token magic link.
  const isVerifyRoute =
    location.pathname.startsWith('/verify/') || location.pathname === '/verify';
  const isSubmitListingsRoute = location.pathname === '/employers/submit-listings';
  // The reviewable Interview Services Agreement the clickwrap links to; a
  // standalone public legal surface, so it ships without the platform nav.
  const isEmployersAgreementRoute = location.pathname === '/employers/agreement';
  // The executive e-sign flow; a standalone chromeless commitment surface.
  const isEmployersStartRoute = location.pathname === '/employers/start';
  const isEmployersCreateRoute = location.pathname === '/employers/create';
  // Public, no-login market report rendered from a token. Like the submit
  // wizard, it is a focused standalone surface sent to one company contact, so
  // it ships without the platform nav/footer.
  const isListingReportRoute = location.pathname.startsWith('/listing-report/');
  // Public, no-login recruiter portfolio market report rendered from a
  // token. Same rationale as isListingReportRoute: a focused standalone
  // surface sent to one recruiter contact, so it ships without the platform
  // nav/footer.
  const isRecruiterReportRoute = location.pathname.startsWith('/recruiter-report/');
  const isWelcomePage = location.pathname === '/welcome' || location.pathname.startsWith('/welcome/') || location.pathname.startsWith('/login/') && location.pathname.includes('/');
  // /pricing renders its own LandingNavbar and Footer. Without this it got both
  // that and the platform NavigationBar, stacked, which read as two headers.
  const isPricingPage = location.pathname === '/pricing';

  // Determine if we should show negotiations tools based on role and auth status
  const showNegotiationTools = auth?.token && auth?.user?.role === ROLES.GROUP;

  // Hide public nav/footer on pages that have their own navigation
  const hideNavAndFooter = isDemoPage || isProfilePage || isGroupDashboardPage || isNotificationsPage || isLandingPage || isRegistrationPage || isNegotiationsRoute || isInterviewFeedbackRoute || isVaultOrVerification || isProviderApp || isShareLanding || isToolsPage || isFniGuestRoute || isPublishDraftRoute || isRecruiterRateRoute || isVerifyRoute || isSubmitListingsRoute || isEmployersAgreementRoute || isEmployersStartRoute || isEmployersCreateRoute || isListingReportRoute || isRecruiterReportRoute || isWelcomePage || isPricingPage || location.pathname === '/start';

  return (
    <div style={{ paddingBottom: isDemoPage && !isProfilePage ? '100px' : 0 }}>
      {/* Always-on bar while an admin is acting as a managed company */}
      <ImpersonationBanner />
      {/* Global banner for demo users */}
      <DemoUserBanner />
      {/* hide nav/footer on demo, profile-new, group dashboard, landing, registration, notifications, and negotiations pages */}
      {!hideNavAndFooter && <NavigationBar />}
      {children}
      {!hideNavAndFooter && <Footer />}

      {/* Add persistent negotiation tools for GROUP users */}
      {showNegotiationTools && (
        <>
          {/* <ActiveNegotiationsIndicator /> */}
        </>
      )}

      {/* Asks BOTH sides whether a scheduled interview happened. Mounted
          app-wide rather than on one layout, because a provider and a
          recruiter are asked about the same interview and it takes both
          answers to complete it. Renders nothing without a session. */}
      <InterviewFeedbackPrompt />

      {/* Dev-only dedupe/SWR cache watcher. Opt-in via
          localStorage.setItem('rxr_show_dedupe_stats', '1'). */}
      <ApiDedupeStatsPanel />
    </div>
  );
};

function App() {
  const [flagsLoaded, setFlagsLoaded] = useState(false);
  // Safe to call again after a privacy-excluded SPA entry; the initializer
  // keeps credentials out of external tags and configures each queue once.
  useEffect(() => {
    initializeGoogleTag();
  }, []);

  // Capture UTM parameters from the landing URL into sessionStorage
  useEffect(() => {
    if (isExternalTrackingBlockedPath(window.location.pathname)) return;
    captureUtmParams();
    // Publish session context (UTM + referrer + click IDs) to the dataLayer
    // BEFORE any page-level analytics fires. GTM DLVs pick these up per-event.
    attachSessionContext();
  }, []);

  // Initialize Meta Pixel globally for the app's direct events (ViewContent,
  // Lead, ...). PageView comes from GTM.
  useEffect(() => {
    if (isExternalTrackingBlockedPath(window.location.pathname)) return;
    import('./utils/metaPixel').then(({ initPixel }) => initPixel());
  }, []);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });

    // Initialize feature flag service
    const flagsmithEnvId = process.env.REACT_APP_FLAGSMITH_ENV_ID || 'development_environment';

    featureFlagService.init(flagsmithEnvId).then(async () => {
      // Force a fresh fetch to avoid stale defaults
      await featureFlagService.updateFlags();
      console.log('Feature flags initialized');
    }).catch((error) => {
      console.error('Failed to initialize feature flags:', error);
    }).finally(() => {
      // Regardless of success or failure, let the app render
      setFlagsLoaded(true);
    });
  }, []);

  if (!flagsLoaded) {
    // Optionally render a loader
    return null;
  }

  return (
    <DebugProvider>
      <AuthProvider>
        {/* Mounts the re-auth modal that services the backend's
            `step_up_required`. Inside AuthProvider so it can swap the stored
            token; above the router so any route's blocked call can be retried. */}
        <StepUpProvider>
        <NotificationProvider>
          <SwipeQueueProvider>
            <InterviewRequestProvider>
            <ContractStatusProvider>
              <Router>
              <ScrollToTop />
              <RegistrationPageViewTracker />
              <JobFeedApprovalBridge />
              <Layout>
                <RouteErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  {/*
                    Wraps the entire route tree, not just /company/* the way
                    GroupApprovalGate does: an admin-invited GROUP teammate who
                    hasn't accepted ToS yet can reach /negotiations/:matchId and
                    /notifications outside /company/*, and this has to catch
                    those too. Resolves before GroupApprovalGate (which sits
                    deeper, inside the /company/* route), so ToS acceptance is
                    always checked first. A no-op for every other visitor:
                    requiresTosAcceptance is only ever true post-cutoff and
                    unaccepted, which self-registration already prevents at
                    signup time.
                  */}
                  <TosAcceptanceGate>
                  {/*
                    Forced pipeline review. Sits INSIDE TosAcceptanceGate on
                    purpose: a user who owes both accepts terms first, because
                    ToS governs whether they may take product actions at all
                    and a disposition IS a product action. Like the ToS gate it
                    wraps the whole route tree rather than just /company/*, so a
                    recruiter cannot sidestep the queue by going straight to
                    /negotiations/:id or /notifications. It is a no-op for every
                    non-GROUP visitor, and it FAILS OPEN if its check cannot
                    load — see PipelineReviewGate for why.
                  */}
                  <PipelineReviewGate>
                    <MedicalStudentAccessGate>
                    <Routes>
                  {/* Public routes - anyone can access */}
                  {/*
                    `/` always redirects to /login. In production, rxrooster.com is
                    served by the Vercel marketing site — this React app (app.rxrooster.com)
                    never renders at `/`. Locally we mirror that: if you want the
                    marketing site, run `npm run dev` in marketing/ and open :3002.
                  */}
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  {/* The admin-invite-then-first-login ToS interstitial. Not
                      role-gated: any authenticated user TosAcceptanceGate
                      redirects here can land on it regardless of role. */}
                  <Route path="/accept-terms" element={<AcceptTerms />} />
                  <Route
                    path="/interview-feedback/:token"
                    element={<InterviewFeedbackMagicPage />}
                  />
                  <Route path="/employers" element={<GroupLandingPage />} />
                  <Route path="/providers" element={<ProviderEntry><ProvidersLandingPage /></ProviderEntry>} />
                  {/* The company claim door: the registration flow opened on
                      its invite-code screen (?code= auto-redeems). One flow,
                      two doors. */}
                  <Route path="/join" element={<GroupRegistration />} />
                  <Route
                    path="/feed-claim/:token"
                    element={<FeedRequisitionHealthPage />}
                  />
                  <Route path="/lp/crna" element={<CrnaLandingPage />} />
                  <Route path="/about" element={<NewAboutPage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/white-paper" element={<WhitePaperDownload />} />
                  {/* Public tools */}
                  <Route path="/tools/watermark-cv" element={<PublicWatermarkPage />} />
                  <Route path="/tools/resume-builder" element={<PublicResumeBuilderPage />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/mcp-publication" element={<McpPublicationArrival />} />
                  <Route path="/mcp/oauth/callback" element={<McpOAuthCallbackPage />} />
                  <Route path="/register" element={<RegisterRouter />} />
                  <Route path="/register/provider" element={<ProviderRegistration />} />
                  {GUIDED_ONBOARDING_ENABLED && (
                    <Route path="/start" element={<StartFunnel />} />
                  )}
                  <Route path="/coverage/continue" element={<CoverageArrival />} />
                  <Route path="/register/company" element={<GroupRegistration />} />
                  <Route path="/register-invite" element={<UserRegistrationInvite />} />
                  <Route path="/welcome" element={<MetaLeadBridgePage />} />
                  <Route path="/welcome/:token" element={<WelcomeFromLeadPage />} />
                  <Route path="/login/:token" element={<WelcomeFromLeadPage />} />
                  <Route path="/welcome-error" element={<WelcomeErrorPage />} />
                  <Route
                    path="/billing-management"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <BillingManagementPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/market-insights" element={<MarketInsightsPage />} />
                  {/* RETIRED: /reports and /reports/success sold standalone comp
                      reports at $1,000 / $2,500 / $5,000 against an unguarded
                      backend route. Comp reports attach to a LISTING now, ten on
                      Free and every listing on Premium, and Premium is the only
                      paid product. Nothing ever linked here; the pages are
                      unreachable rather than deleted, so past purchase records
                      and their download tokens are untouched. */}
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  {/* Short invite links */}
                  <Route path="/i/:code" element={<InviteRedirect />} />

                  {/* Referral links */}
                  <Route path="/r/:code" element={<ReferralLanding />} />

                  {/* OAuth callback */}
                  <Route path="/oauth-callback" element={<OAuthCallback />} />

                  {/* Magic-login landing: backend /auth/magic/:token (API
                      origin) consumes the one-shot token, mints a JWT, and
                      redirects here with ?token=<jwt>&redirect=<path> (audit #32). */}
                  <Route path="/auth/magic" element={<MagicLoginCallback />} />
                  {/* Registered-provider lifecycle email landing. The raw
                      credential reaches the API only through a POST, then the
                      constrained provider session deep-links to the destination
                      stored by the backend. */}
                  <Route
                    path="/auth/provider-email"
                    element={<ProviderEmailMagicLogin />}
                  />

                  {/* SEO-optimized listing pages */}
                  <Route path="/jobs/:slug" element={<ListingSeoPage />} />

                  {/* Public share links */}
                  <Route path="/s/:token" element={<ShareLanding />} />
                  <Route path="/s/:token/services" element={<ProviderServicesShare />} />
                  {/* The recruiter side of a shared provider card: post a role
                      that meets the terms the card publishes, and the gate opens
                      a no-login conversation. */}
                  <Route path="/s/:token/role" element={<ShareRoleGate />} />
                  {/* Legacy GasWork links open the standard share page. The
                      anonymous provider deck that lived here was retired. */}
                  <Route path="/g/:token" element={<GasWorkShareRedirect />} />
                  {/* Recruiter daily-matches deck: 24h magic link. Own prefix
                      (/rm/) so it does not collide with the provider referral
                      route /r/:code above, which would otherwise shadow it. */}
                  <Route path="/rm/:token" element={<RecruiterMatchesDeck />} />
                  {/* Provider daily-matches deck: 24h magic link, twin of /rm/.
                      Exchanges the token for a real session (POST
                      /provider-matches/session), or reuses an already-signed-in
                      CRNA session, then renders a self-contained deck of the
                      provider's top matched roles (Skip / Request interview).
                      It never loads the /provider app shell. */}
                  <Route path="/pm/:token" element={<ProviderMatchesDeck />} />
                  {/* Legacy claim surface retired: 301 to the modern FNI claim
                      funnel (audit #34). The old ClaimListingPage leaked PII over
                      an unauthenticated GET and dead-ended at /dashboard. */}
                  <Route path="/claim/:token" element={<LegacyClaimRedirect />} />
                  <Route path="/claim-listing/:token" element={<ExternalListingClaimPage />} />
                  {/* FNI-5: guest-scoped negotiation entry for FNI email recipients */}
                  <Route path="/negotiate/invite/:token" element={<GuestNegotiationPage />} />
                  {/* Wild Job Capture: the recruiter opens the card an admin
                      built from her own posting, fills the blanks, and
                      publishes it by registering. Public and token-only, like
                      the claim and guest-negotiation routes above: the token IS
                      the authorization on the read, the autosave and the
                      publish. No provider PII. */}
                  <Route path="/publish/:token" element={<PublishListingPage />} />
                  {/* Comp-blind rate capture (Phase 2): no-login recruiter
                      rate-entry surface reached from the interest email magic
                      link. Recruiter-facing, no provider PII. */}
                  <Route path="/rate/:token" element={<RecruiterRatePage />} />
                  {/* Recruiter listing verification: no-login, magic-link only.
                      Reached from the VERIFY_LISTING email or the reactive CTA.
                      Recruiter-facing; no provider PII. */}
                  <Route path="/verify/:token" element={<VerifyListingPage />} />
                  {/* Friendlier second door into the same no-login flow: a short
                      entry code (instead of the 64-char token URL) that a
                      recruiter can type from the email. Exchanges the code for
                      a token, then hands off to /verify/:token above. */}
                  <Route path="/verify" element={<VerifyCodePage />} />
                  {/* Recruiter listing-health-report: durable magic link, admin-pushed via
                      Dittofeed. Own prefix, sibling to /rm/ and /pm/. */}
                  <Route
                    path="/listing-health-report/:token"
                    element={<ListingHealthReportPage />}
                  />
                  {/* SMS re-permission. Public and unauthenticated on purpose:
                      recipients include people who never finished a profile,
                      and requiring a login to answer a consent question would
                      depress response. The page reads status on load but only
                      ever consents from an explicit button press. */}
                  <Route path="/sms-confirm/:token" element={<SmsConfirmPage />} />

                  {/* Self-service listing intake: public, no account */}
                  <Route path="/employers/submit-listings" element={<SubmitListingsPage />} />
                  <Route path="/employers/agreement" element={<EmployersAgreementPage />} />
                  <Route path="/employers/start" element={<EmployerSignPage />} />
                  <Route path="/employers/create" element={<EmployersCreatePage />} />

                  {/* Public, no-login market report rendered from a token */}
                  <Route path="/listing-report/:token" element={<ListingReportPage />} />

                  {/* Public, no-login recruiter portfolio market report: one
                      durable link covers a recruiter's whole portfolio */}
                  <Route path="/recruiter-report/:token" element={<RecruiterMarketReportPage />} />

                  {/* SEO city landing pages */}
                  <Route path="/crna-jobs/:citySlug" element={<CityJobsPage />} />

                  {/* SEO state landing pages — 50 states + DC */}
                  <Route path="/crna-jobs/state/:stateSlug" element={<StateJobsPage />} />

                  {/* CRNA salary by state pages — Tier 2 keyword target */}
                  <Route path="/resources/crna-salary/:stateSlug" element={<CrnaSalaryPage />} />

                  {/* CRNA practice authority by state pages */}
                  <Route path="/resources/crna-practice-authority/:stateSlug" element={<CrnaPracticeAuthorityPage />} />

                  {/* CRNA market overview — knowledge hub / pillar page */}
                  <Route path="/resources/crna-jobs-guide" element={<CrnaMarketOverview />} />

                  {/* ── CRNA Market Reports (national + state) ── */}
                  <Route path="/resources/market-report" element={<MarketReportPage />} />
                  <Route path="/resources/market-report/:stateSlug" element={<StateMarketReportPage />} />

                  {/* Blog */}
                  <Route path="/blog" element={<BlogListPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />

                  {/* Unsubscribe from email notifications */}
                  <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />

                  {/* Secure share link access (password-protected document downloads) */}
                  <Route path="/dl/:token" element={<SecureShareLinkPage />} />

                  {/* Payment result pages */}
                  <Route path="/payment-success" element={<PaymentSuccessPage />} />
                  <Route path="/payment-cancel" element={<PaymentCancelPage />} />

                  {/* Legacy profile routes — redirect to new provider profile */}
                  <Route path="/profile" element={<Navigate to="/provider/profile" replace />} />
                  <Route path="/profile-new" element={<Navigate to="/provider/profile" replace />} />

                  {/* Redirect /vault and /verification to provider sub-pages */}
                  <Route path="/vault" element={<Navigate to="/provider/vault" replace />} />
                  <Route path="/verification" element={<Navigate to="/provider/profile" replace />} />

                  <Route path="/invites" element={<RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized"><InvitesPage /></RoleBasedRoute>} />

                  {/* /contract-tracker removed per PRODUCT-ETHOS.md D9 (audit P1) */}

                  <Route
                    path="/negotiations/:matchId"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <NegotiationsRouteGuard />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/notifications"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <NotificationsPage />
                      </RoleBasedRoute>
                    }
                  />

                  {/* ─── Company Routes — Nested under CompanyLayout ─── */}
                  <Route
                    path="/company/*"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <CompanyProvider>
                          <CompanyLayout>
                            <GroupApprovalGate>
                              <Routes>
                                {/* The sign-in landing. Resolves to the provider
                                    deck when today's curated matches still have
                                    unswiped cards, and to the dashboard
                                    otherwise. See CompanyLandingRedirect for why
                                    /company (bare) is the only safe place to
                                    make that choice. */}
                                <Route index element={<CompanyLandingRedirect />} />
                                <Route path="dashboard" element={<CompanyDashboardPage />} />
                                <Route path="facilities" element={<Navigate to="/company/listings" replace />} />
                                <Route path="listings" element={<CompanyListingsPage />} />
                                <Route path="mcp-publication" element={<McpPublicationReviewPage />} />
                                {/* The per-listing report, REPARENTED under
                                    listings. The portfolio page it used to sit
                                    beside is gone (its content is the market
                                    columns on the roster), and with the nav item
                                    gone too the old path lit up no sidebar entry
                                    at all. Under /company/listings the sidebar
                                    keeps Listings active, which is where the
                                    reader came from and where Back returns them.
                                    The old path is kept as a redirect: it shipped
                                    in a build and may be bookmarked. */}
                                {/* The briefing itself is addressed by an unguessable slug, so no
                                    listing ObjectId ever sits in the address bar or in a bookmark. */}
                                <Route path="reports/:slug" element={<CompanyListingReportPage />} />
                                {/* Both id-addressed paths resolve the slug and replace the history
                                    entry with it. Kept rather than dropped: the id path shipped in a
                                    build and the roster still opens the report by row id. */}
                                <Route path="listings/:listingId/market-report" element={<CompanyReportResolver />} />
                                <Route path="market-report/:listingId" element={<LegacyMarketReportRedirect />} />
                                <Route path="market-report" element={<Navigate to="/company/listings" replace />} />
                                <Route path="providers" element={<CompanyProvidersPage />} />
                                <Route path="coverage" element={<CompanyProvidersPage />} />
                                <Route path="providers/:listingId" element={<CompanyProvidersPage />} />
                                {AVAILABILITY_SEARCH_ENABLED && (
                                  <Route path="find-providers" element={<FindProvidersPage />} />
                                )}
                                <Route path="pipeline" element={<CompanyNegotiationsPage />} />
                                {PARTNER_CONNECTIONS_ENABLED && (
                                  <Route path="coverage-integrations" element={<CoverageConnections />} />
                                )}
                                <Route path="coverage-requests" element={<Navigate to="/company/pipeline" replace />} />
                                {/* Declared BEFORE pipeline/:id, or the param
                                    route swallows "review" as an id. */}
                                <Route path="pipeline/review" element={<CompanyPipelineReviewPage />} />
                                <Route path="pipeline/:id" element={<CompanyNegotiationDetailPage />} />
                                {/* Legacy redirect */}
                                <Route path="negotiations" element={<Navigate to="/company/pipeline" replace />} />
                                <Route path="negotiations/:id" element={<Navigate to="/company/pipeline" replace />} />
                                <Route path="settings" element={<CompanySettingsPage />} />
                                <Route
                                  path="job-feed/:feedId/health-report"
                                  element={<FeedRequisitionHealthPage authenticated />}
                                />
                                <Route path="notifications" element={<CompanyNotificationsPage />} />
                                {/* /company/schedule-check is deliberately NOT routed: the
                                    Coverage optimizer is not a live feature, so the URL falls
                                    through to the dashboard redirect in every environment. */}
                                <Route path="*" element={<Navigate to="/company/dashboard" replace />} />
                              </Routes>
                            </GroupApprovalGate>
                          </CompanyLayout>
                        </CompanyProvider>
                      </RoleBasedRoute>
                    }
                  />

                  {/* Legacy company route redirects */}
                  <Route path="/company-intake" element={<Navigate to="/company/listings" replace />} />
                  <Route path="/company-provider-swipe" element={<Navigate to="/company/providers" replace />} />
                  <Route path="/company-provider-swipe/:listingId" element={<Navigate to="/company/providers" replace />} />

                  {/* /contracts/:contractId/sign removed per PRODUCT-ETHOS.md D9 (audit P2):
                      it generated a placeholder-named "$25,000 binding" PDF client-side with
                      no backend persistence. */}


                  {/* ADMIN Only Routes */}
                  <Route path="/admin/referral-activity" element={<ReferralActivityRoute />} />
                  <Route path="/admin/referral-shirt-gifts" element={<RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized"><ReferralShirtRewardsPage /></RoleBasedRoute>} />
                  <Route path="/admin/coverage-partners" element={<RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized"><CoverageAdmin /></RoleBasedRoute>} />
                  <Route
                    path="/admin/job-feeds/:feedId/health-report"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <FeedRequisitionHealthPage authenticated admin />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <>
                          <AuthDebug />
                          <AdminDashboard />
                        </>
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/admin/users"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <AdminUsersPage />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/admin/visitor-map"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <VisitorMapPage />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/admin/provider-registration-experiment"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderRegistrationExperimentPage />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/admin/at-risk-negotiations"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <AtRiskNegotiationsPage />
                      </RoleBasedRoute>
                    }
                  />


                  <Route
                    path="/admin/listing-submissions"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ListingSubmissionsList />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/admin/listing-submissions/:submissionId"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ListingSubmissionDetail />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/admin/vendor-docs-requests"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <VendorDocsRequestsList />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/admin/listing-suppressions"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ListingSuppressionsPage />
                      </RoleBasedRoute>
                    }
                  />

                  {[
                    '/admin/mcp-publication-operations',
                    '/admin/mcp-publication-authorities',
                  ].map((path) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                          <McpPublicationOperationsPage />
                        </RoleBasedRoute>
                      }
                    />
                  ))}

                  <Route
                    path="/admin/employer-support"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ListingSuppressionsPage />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/admin/wild-capture"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <AdminWildCapturePage />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/listings/review"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ListingReviewPage />
                      </RoleBasedRoute>
                    }
                  />

                  {/* Marketing role routes (ADMIN + MARKETING) */}
                  <Route
                    path="/marketing/*"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.MARKETING]} redirectTo="/unauthorized">
                        <MarketingShell />
                      </RoleBasedRoute>
                    }
                  >
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<MarketingOverview />} />
                    <Route path="jobs" element={<MarketingJobSocialLibrary />} />
                    <Route path="analytics" element={<MarketingAnalytics />} />
                    <Route path="tracker" element={<MarketingTracker />} />
                    <Route path="negotiations" element={<MarketingNegotiations />} />
                    <Route path="users" element={<MarketingUsers />} />
                    <Route path="users/:id" element={<MarketingUserProfile />} />
                    <Route path="providers/:providerId" element={<MarketingUserProfile />} />
                    <Route path="links" element={<MarketingLinks />} />
                    <Route path="outreach" element={<MarketingOutreach />} />
                    <Route path="command" element={<MarketingCommand />} />
                    {/* Inbound leads routes - ADMIN + MARKETING, static paths before :id param */}
                    <Route path="inbound-leads" element={<RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.MARKETING]} redirectTo="/unauthorized"><InboundLeadsList /></RoleBasedRoute>} />
                    <Route path="inbound-leads/funnel" element={<RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.MARKETING]} redirectTo="/unauthorized"><InboundLeadFunnel /></RoleBasedRoute>} />
                    <Route path="inbound-leads/new" element={<RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.MARKETING]} redirectTo="/unauthorized"><ManualLeadEntry /></RoleBasedRoute>} />
                    <Route path="inbound-leads/import" element={<RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.MARKETING]} redirectTo="/unauthorized"><InboundLeadCsvImport /></RoleBasedRoute>} />
                    <Route path="inbound-leads/:id" element={<RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.MARKETING]} redirectTo="/unauthorized"><InboundLeadDetail /></RoleBasedRoute>} />
                  </Route>

                  <Route
                    path="/admin/team"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.ADMIN]} redirectTo="/unauthorized">
                        <MarketingShell>
                          <AdminTeam />
                        </MarketingShell>
                      </RoleBasedRoute>
                    }
                  />

                  {/* Demo pages - available to all */}
                  <Route path="/demo" element={<DemoLandingPage />} />
                  <Route path="/demo/crna" element={<CRNADemoPage />} />
                  <Route path="/demo/scheduler" element={<SchedulerDemoPage />} />
                  <Route path="/auth-debug" element={<AuthDebug />} />
                  <Route
                    path="/test-group-intake"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.GROUP, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <TestGroupIntake />
                      </RoleBasedRoute>
                    }
                  />

                  <Route
                    path="/provider/student"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <MedicalStudentAnesthesiologyPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/dashboard"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderDashboardV2 />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/profile"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderProfilePage />
                      </RoleBasedRoute>
                    }
                  />
                  {/* Catch-all for unbuilt /provider/profile/* sub-routes — redirect to main profile */}
                  <Route path="/provider/profile/*" element={<Navigate to="/provider/profile" replace />} />
                  <Route
                    path="/provider/discover"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProfileCompletionGuard allowReferralReward>
                          <ProviderDiscoverPage />
                        </ProfileCompletionGuard>
                      </RoleBasedRoute>
                    }
                  />
                  {/* Retired duplicate surface. Keep links already in the wild
                      working while Discover owns authenticated match review. */}
                  <Route
                    path="/provider/matches"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderMatchesRedirect />
                      </RoleBasedRoute>
                    }
                  />
                  {/* No ProfileCompletionGuard here: these rows exist because a
                      facility already acted on this provider. They are not
                      computed from the provider's own preferences, so an
                      incomplete profile is no reason to bounce them to the
                      wizard, and doing so would stall a facility that is
                      waiting on their answer. */}
                  <Route
                    path="/provider/interested"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <Navigate to="/provider/pipeline" replace />
                      </RoleBasedRoute>
                    }
                  />
                  <Route path="/provider/coverage-requests" element={<Navigate to="/provider/pipeline" replace />} />
                  <Route
                    path="/provider/pipeline"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        {/* Recruiter-originated interest requires a timely answer.
                            Do not bounce an invited provider to profile setup
                            before they can see and respond to the role. */}
                        <ProviderPipelinePage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/vault"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProfileCompletionGuard>
                          <ProviderVaultPage />
                        </ProfileCompletionGuard>
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/listing/:id"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderListingDetailPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/negotiation/:id"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderNegotiationDetailPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/notifications"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderNotificationsPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/financial"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderFinancialPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/map"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderMapExplorerPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/edit-preferences"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderProfileEditorPage />
                      </RoleBasedRoute>
                    }
                  />
                  {PROVIDER_SCHEDULE_ENABLED && (
                    <>
                    <Route path="/provider/services" element={<RoleBasedRoute allowedRoles={[ROLES.CRNA]} redirectTo="/unauthorized"><ProfileCompletionGuard><ProviderServicesPage /></ProfileCompletionGuard></RoleBasedRoute>} />
                    <Route path="/provider/services/preview" element={<RoleBasedRoute allowedRoles={[ROLES.CRNA]} redirectTo="/unauthorized"><ProfileCompletionGuard><ProviderServicesPage preview /></ProfileCompletionGuard></RoleBasedRoute>} />
                    <Route path="/provider/schedule" element={<ProviderScheduleRedirect />} />
                    </>
                  )}
                  {PROVIDER_ROUNDS_ENABLED && (
                    <Route
                      path="/provider/rounds"
                      element={
                        <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                          <ProfileCompletionGuard>
                            <ProviderRoundsPage />
                          </ProfileCompletionGuard>
                        </RoleBasedRoute>
                      }
                    />
                  )}
                  {ROOST_ENABLED && (
                    <Route
                      path="/provider/roost"
                      element={
                        <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                          <ProfileCompletionGuard>
                            <RoostPage />
                          </ProfileCompletionGuard>
                        </RoleBasedRoute>
                      }
                    />
                  )}
                  <Route
                    path="/provider/compare"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderComparisonPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="/provider/credential/:id"
                    element={
                      <RoleBasedRoute allowedRoles={[ROLES.CRNA, ROLES.ADMIN]} redirectTo="/unauthorized">
                        <ProviderCredentialDetailPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route path="/provider" element={<Navigate to="/provider/dashboard" replace />} />

                  {/* Provider sub-view aliases (BUG-024) */}
                  <Route path="/settings" element={<Navigate to="/provider/profile" replace />} />
                  <Route path="/discover" element={<Navigate to="/provider/discover" replace />} />

                  {/* Logout route (BUG-025) */}
                  <Route path="/logout" element={<LogoutRedirect />} />

                  {/* Catch-all 404 (BUG-022) */}
                  <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </MedicalStudentAccessGate>
                  </PipelineReviewGate>
                  </TosAcceptanceGate>
                </Suspense>
                </RouteErrorBoundary>
              </Layout>
            </Router>
            {/* Toast notifications container - must be inside Router for navigation */}
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            </ContractStatusProvider>
            </InterviewRequestProvider>
          </SwipeQueueProvider>
        </NotificationProvider>
        </StepUpProvider>
      </AuthProvider>
    </DebugProvider>
  );
}

export default App;
